import {
  fetchGontijoClientDetails,
  type GontijoAccountDetails,
} from '@/services/gontijo-session/gontijoAuthApi';

interface CachedAccountDetails {
  token: string | null;
  request: Promise<GontijoAccountDetails | null> | null;
}

/**
 * One read per token, shared by every passenger on the screen.
 *
 * Keyed by the token so a re-login never hands the previous buyer's record to the next one. The
 * promise is what is cached, not the record, so passengers mounting together wait on the same
 * request instead of each opening their own.
 */
let cached: CachedAccountDetails = { token: null, request: null };

/** The record behind a token, read at most once. Null when there is none. */
function readGontijoAccountDetails(token: string): Promise<GontijoAccountDetails | null> {
  if (cached.token === token && cached.request) return cached.request;

  const request = fetchGontijoClientDetails(token).then((details) => {
    // A failed read is not held: the next mount asks again, so a blip costs one round trip and not
    // the offer for the rest of the session.
    if (!details && cached.request === request) cached = { token: null, request: null };

    return details;
  });
  cached = { token, request };

  return request;
}

/** Test seam: forget the cached read. */
function resetGontijoAccountDetails(): void {
  cached = { token: null, request: null };
}

export { readGontijoAccountDetails, resetGontijoAccountDetails };
